import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Section, Eyebrow } from '@/components/primitives/Container';
import { Button } from '@/components/primitives/Button';
import { cn } from '@/lib/cn';

type Profile = 'docente' | 'directivo' | 'especialista' | 'desarrollador' | 'otro';

const PROFILES: { id: Profile; label: string }[] = [
  { id: 'docente', label: 'Docente' },
  { id: 'directivo', label: 'Directivo / supervisor' },
  { id: 'especialista', label: 'Especialista o equipo técnico ministerial' },
  { id: 'desarrollador', label: 'Desarrollador de IA' },
  { id: 'otro', label: 'Otro' },
];

export function Contacto() {
  const [searchParams] = useSearchParams();
  const initialProfile = (searchParams.get('perfil') as Profile | null) ?? null;

  const [profile, setProfile] = useState<Profile | null>(
    initialProfile && PROFILES.some((p) => p.id === initialProfile) ? initialProfile : null,
  );
  const [submitted, setSubmitted] = useState(false);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <>
      <Section className="pt-12 sm:pt-16">
        <Container width="editorial">
          <Eyebrow>Contacto</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-normal tracking-tight sm:text-5xl">
            ¿Quién sos?
          </h1>
          <p className="mt-6 max-w-prose text-lg leading-relaxed text-ink-muted">
            Tu respuesta orienta el formulario y el equipo que recibe tu mensaje.
          </p>
        </Container>
      </Section>

      <Section className="pt-0">
        <Container width="editorial">
          {!submitted ? (
            <>
              <fieldset>
                <legend className="sr-only">Elegí tu perfil</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {PROFILES.map((p) => (
                    <label
                      key={p.id}
                      className={cn(
                        'focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-2 cursor-pointer rounded-lg border p-4 transition-colors',
                        profile === p.id
                          ? 'border-ink bg-ink text-paper'
                          : 'border-line bg-paper text-ink hover:border-line_strong',
                      )}
                    >
                      <input
                        type="radio"
                        name="profile"
                        value={p.id}
                        checked={profile === p.id}
                        onChange={() => setProfile(p.id)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{p.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {profile && (
                <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-5">
                  <Field label="Nombre" required>
                    <input
                      type="text"
                      name="name"
                      required
                      className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                    />
                  </Field>
                  {(profile === 'directivo' || profile === 'especialista') && (
                    <Field label="Institución / Equipo">
                      <input
                        type="text"
                        name="institution"
                        className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                      />
                    </Field>
                  )}
                  <Field label="¿Qué necesitás?" required>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                      placeholder="Contanos brevemente. No hace falta dar contexto técnico."
                    />
                  </Field>
                  <label className="flex items-start gap-2 text-xs text-ink-muted">
                    <input type="checkbox" required className="mt-0.5" />
                    <span>
                      Acepto que me contacten por correo. No comparten mis datos con terceros.
                    </span>
                  </label>
                  <div>
                    <Button type="submit">Enviar</Button>
                  </div>
                </form>
              )}
            </>
          ) : (
            /* Este formulario no envía a ningún lado: no hay endpoint, y la
               página legal declara que los datos de contacto institucional
               están pendientes de publicación. Decía "Recibimos tu mensaje",
               que era falso. Mientras no haya canal, la pantalla lo dice. */
            <div role="status" className="rounded-lg border border-line bg-paper-tinted/40 p-6">
              <p className="font-serif text-lg text-ink">
                No pudimos enviar tu mensaje: todavía no tenemos el canal abierto.
              </p>
              <p className="mt-3 text-base leading-relaxed text-ink-muted">
                Nada de lo que escribiste se guardó ni viajó a ningún lado. Estamos terminando de
                publicar los datos de contacto institucional; cuando estén, van a aparecer en la
                página legal junto con el responsable.
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink-muted">
                Preferimos decírtelo antes que dejarte esperando una respuesta que no iba a llegar.
              </p>
            </div>
          )}
        </Container>
      </Section>

      <Section ariaLabelledby="canales-title" className="border-t border-line">
        <Container width="editorial">
          <Eyebrow>Canales adicionales</Eyebrow>
          <h2 id="canales-title" className="mt-3 font-serif text-2xl font-normal tracking-tight">
            Otros caminos.
          </h2>
          {/* La página legal declara hoy "datos de contacto institucional
              pendientes de publicación", así que mandar a alguien ahí a buscar
              un correo directo es mandarlo a una puerta cerrada. */}
          <p className="prose-editorial mt-6 text-sm">
            El correo directo del editor responsable todavía no está publicado. Va a aparecer en{' '}
            <a href="/legal#editor" className="focus-ring rounded text-accent hover:underline">
              la página legal
            </a>{' '}
            junto con la razón social, en cuanto se ratifique.
          </p>
        </Container>
      </Section>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink">
        {label}
        {required && <span className="ml-1 text-status-progress">*</span>}
      </span>
      {children}
    </label>
  );
}
